import React from 'react';
import Avatar from '@atlaskit/avatar';
import AvatarItem from '@atlaskit/avatar/AvatarItem';
import Button from '@atlaskit/button';
import PeopleIcon from '@atlaskit/icon/core/migration/people-group--people';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { FormattedMessage } from '@atlassian/jira-intl';
import type { RequestTypeSupportedPrincipalType } from '../../../../../../services/load-request-type-restriction/types.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const PrincipalItem = ({ principal, canAccess, isGreyedOut, removePrincipal }: Props) => {
	const avatar = (
		<AvatarItem
			avatar={
				principal.type === 'USER' ? (
					<Avatar
						size="medium"
						name={principal.name}
						src={principal.avatar}
						isDisabled={isGreyedOut}
					/>
				) : (
					<Box xcss={[peopleIconStyles, isGreyedOut && greyedOutPeopleIconStyles]}>
						<PeopleIcon
							LEGACY_size="medium"
							spacing="spacious"
							label=""
							color={token('color.icon')}
						/>
					</Box>
				)
			}
			primaryText={
				<Box as="span" xcss={isGreyedOut && greyedOutTextStyles}>
					{principal.name}
				</Box>
			}
			secondaryText={isGreyedOut ? undefined : getByline(principal.type)}
		/>
	);

	return (
		<Inline>
			<Box xcss={avatarStyles}>{avatar}</Box>
			<Box xcss={[permissionsStyles, isGreyedOut && greyedOutTextStyles]}>
				{canAccess ? (
					<FormattedMessage {...messages.hasAccess} />
				) : (
					<FormattedMessage {...messages.noAccess} />
				)}
			</Box>
			<Box xcss={[removeButtonStyles, isGreyedOut && greyedOutTextStyles]}>
				{removePrincipal && (
					<Button appearance="link" spacing="none" onClick={() => removePrincipal()}>
						<FormattedMessage {...messages.remove} />
					</Button>
				)}
			</Box>
		</Inline>
	);
};

const getByline = (principalType: RequestTypeSupportedPrincipalType) => {
	switch (principalType) {
		case 'USER_GROUP':
			return <FormattedMessage {...messages.group} />;
		case 'CUSTOMER_ORGANIZATION':
			return <FormattedMessage {...messages.customerOrganization} />;
		default:
			return null;
	}
};

const avatarStyles = xcss({
	width: '65%',
	margin: 'auto',
});

const peopleIconStyles = xcss({
	backgroundColor: 'color.background.accent.gray.subtlest',
	borderRadius: 'border.radius.circle',
	margin: 'space.025',
	padding: 'space.050',
});

const greyedOutPeopleIconStyles = xcss({
	opacity: 'opacity.disabled',
});

const permissionsStyles = xcss({
	width: '23%',
	margin: 'auto',
});

const removeButtonStyles = xcss({
	width: '12%',
	margin: 'auto',
});

const greyedOutTextStyles = xcss({
	color: 'color.text.disabled',
});
